import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../api';
import styles from '../assets/usefulInformation.module.css';

const UsefulInformation = () => {
  const navigate = useNavigate();
  const [downloading, setDownloading] = useState({});

  // ✅ UPDATED: Download documents from backend API instead of static frontend files
  // This fixes corruption issues on deployment by serving files from backend storage
  const handleDocumentDownload = async (documentType, documentName) => {
    try {
      setDownloading((prev) => ({ ...prev, [documentType]: true }));

      console.log(`📥 [USEFUL_INFO] Downloading PDF: ${documentName} (${documentType})`);

      // Call backend API to download PDF from backend storage
      // Backend route: /api/settings/useful-information/documents/{documentType}
      try {
        const response = await api.get(`/settings/useful-information/documents/${documentType}`, {
          responseType: 'blob', // Important: receive binary data
        });

        // Create blob from response
        const blob = new Blob([response.data], { type: 'application/pdf' });
        
        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        
        // Extract filename from Content-Disposition header or use document name
        const contentDisposition = response.headers?.['content-disposition'] || response.headers?.['Content-Disposition'];
        let filename = documentName
          .replace(/\s+/g, '_')           // Replace spaces with underscores
          .replace(/[&()]/g, '')         // Remove special characters like &, (, )
          .replace(/_+/g, '_')           // Replace multiple underscores with single underscore
          .replace(/^_|_$/g, '')         // Remove leading/trailing underscores
          .toLowerCase() + '.pdf';
        
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i);
          if (filenameMatch && filenameMatch[1]) {
            // Remove quotes if present
            filename = filenameMatch[1].replace(/['"]/g, '').trim();
            // Ensure it ends with .pdf (remove any extra characters)
            if (!filename.toLowerCase().endsWith('.pdf')) {
              filename = filename.replace(/\.pdf.*$/i, '') + '.pdf';
            }
          }
        }
        
        link.download = filename;
        link.rel = 'noopener noreferrer';
        
        // Trigger download
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Clean up blob URL
        window.URL.revokeObjectURL(url);

        toast.success(`📥 ${documentName} downloaded successfully!`);
        console.log(`✅ [USEFUL_INFO] Successfully downloaded: ${documentName} from backend`);

      } catch (apiError) {
        console.error('❌ [USEFUL_INFO] API error:', apiError);
        
        if (apiError.response?.status === 404) {
          toast.error(`Document not found: ${documentName}. Please contact administrator.`);
        } else {
          toast.error(`Failed to download ${documentName}. Please try again.`);
        }
      }

    } catch (error) {
      console.error('❌ [USEFUL_INFO] Error in document download:', error);
      toast.error('Failed to download document. Please try again.');
    } finally {
      // Reset downloading state after a short delay
      setTimeout(() => {
        setDownloading((prev) => ({ ...prev, [documentType]: false }));
      }, 500);
    }
  };

  const documents = [
    {
      type: 'guardian_handbook',
      name: 'Guardian Handbook',
      description:
        'Comprehensive guide detailing your responsibilities, benefits, and the code of conduct.',
      downloadText: 'click to open',
    },
    {
      type: 'health_and_safety_guidelines',
      name: 'Health & Safety Guidelines',
      description:
        'Important information to ensure your safety and well-being in your guardian property.',
      downloadText: 'click to open',
    },
    {
      type: 'environment_policy',
      name: 'Environmental Policy',
      description:
        'Our commitment to sustainability and how you can contribute.',
      downloadText: 'click to open',
    },
    {
      type: 'guardian_faq',
      name: 'Frequently Asked Questions (FAQs)',
      description: 'Answers to common questions about living as a guardian.',
      downloadText: 'click to open',
    },
  ];

  const journeyDocuments = [
    {
      type: 'living_as_a_guardian',
      name: 'Living as a Guardian',
      downloadText: 'click to open',
    },
    {
      type: 'becoming_a_guardian_guide',
      name: 'Becoming a Guardian Guide',
      downloadText: 'click to open',
    },
    {
      type: 'guardian_onboarding',
      name: 'Guardian Onboarding',
      downloadText: 'click to open',
    },
  ];

  return (
    <div className={styles.container}>
      {/* Breadcrumbs */}
      <nav className={styles.breadcrumb}>
        <ol>
          <li>
            <span onClick={() => navigate('/guardian-dashboard')} style={{ cursor: 'pointer', color: '#337ab7' }}>Dashboard</span>
          </li>
          <li className={styles.active}>Useful Information</li>
        </ol>
      </nav>

      {/* Header with green banner */}
      <div className={styles.headerBanner}>
        <div className={styles.bannerTag}>
          Useful Information & Documentation
        </div>
        <h1 className={styles.title}>Useful information & documentation</h1>
        <h2 className={styles.subtitle}>
          Welcome to the Useful Information & Documentation section!
        </h2>
        <p className={styles.description}>
          Here, you'll find all the necessary resources and guidelines to help
          you understand how we operate and to support you throughout your
          journey with us at Global Guardians.
        </p>
      </div>

      {/* Main Content Area */}
      <div className={styles.mainContent}>
        {/* Key Resources Section */}
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Key Resources</h3>

          {documents.map((doc, index) => (
            <div key={doc.type} className={styles.documentItem}>
              <p className={styles.documentDescription}>
                {index + 1}. {doc.name}: {doc.description}
              </p>
              <div className={styles.documentButtonContainer}>
                <button
                  className={styles.documentButton}
                  onClick={() => handleDocumentDownload(doc.type, doc.name)}
                  disabled={downloading[doc.type]}
                >
                  <div className={styles.documentLeft}>
                    <div className={styles.pdfIconContainer}>
                      <img
                        src="/images/pdf.png"
                        alt="PDF"
                        className={styles.pdfIcon}
                      />
                    </div>
                  </div>
                  <div className={styles.documentRight}>
                    <p className={styles.documentName}>{doc.name}</p>
                    <p className={styles.documentText}>{doc.downloadText}</p>
                  </div>
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* How We Operate Section */}
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>How We Operate</h3>
          <ul className={styles.operationList}>
            <li>
              Property Inspections: Regular random unannounced checks to ensure
              properties are well-maintained and secure.
            </li>
            <li>
              Community Engagement: Initiatives and events to foster a sense of
              community among guardians.
            </li>
            <li>
              Support Services: Assistance available for any issues or concerns
              you may encounter.
            </li>
          </ul>
        </div>

        {/* Guardian Journey Section */}
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Guardian Journey</h3>
          <ul className={styles.journeyList}>
            <li>
              Application Process: Step-by-step guide on how to become a
              guardian.
            </li>
            <li>
              Onboarding: What to expect during your first weeks as a guardian.
            </li>
            <li>
              Living in a Property: Tips and best practices for making the most
              of your guardian experience.
            </li>
          </ul>

          <div className={styles.journeyDocuments}>
            {journeyDocuments.map((doc) => (
              <div key={doc.type} className={styles.journeyDocumentItem}>
                <button
                  className={styles.documentButton}
                  onClick={() => handleDocumentDownload(doc.type, doc.name)}
                  disabled={downloading[doc.type]}
                >
                  <div className={styles.documentLeft}>
                    <div className={styles.pdfIconContainer}>
                      <img
                        src="/images/pdf.png"
                        alt="PDF"
                        className={styles.pdfIcon}
                      />
                    </div>
                  </div>
                  <div className={styles.documentRight}>
                    <p className={styles.documentName}>{doc.name}</p>
                    <p className={styles.documentText}>{doc.downloadText}</p>
                  </div>
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Additional Information Section */}
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Guardian Journey</h3>
          <p className={styles.additionalInfo}>
            For further assistance, please reach out to us by clicking on the
            Have a Query link in the tab above. We're here to help you every
            step of the way.
          </p>
          <p className={styles.thankYou}>
            Thank you for being a part of our mission to turn empty properties
            into vibrant communities!
          </p>
        </div>
      </div>
    </div>
  );
};

export default UsefulInformation;
